import {
  createContext, useContext, useState, ReactNode,
  useMemo,
} from 'react';

interface FilterDrawerContextType {
  isOpen: boolean;
  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export const FilterDrawerContext = createContext<FilterDrawerContextType | undefined>(undefined);

interface FilterDrawerProviderProps {
  children: ReactNode;
}

/**
 * Provider for filter drawer state management
 */
export const FilterDrawerProvider = ({ children }: FilterDrawerProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDrawer = () => setIsOpen((prev) => !prev);
  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  const value = useMemo(() => ({
    isOpen,
    toggleDrawer,
    openDrawer,
    closeDrawer,
  }), [isOpen]);

  return (
    <FilterDrawerContext.Provider value={value}>
      {children}
    </FilterDrawerContext.Provider>
  );
};

/**
 * Hook to use the filter drawer context
 */
export const useFilterDrawer = () => {
  const context = useContext(FilterDrawerContext);
  if (context === undefined) {
    throw new Error('useFilterDrawer must be used within a FilterDrawerProvider');
  }
  return context;
};
